/**
 * Design System Theme Constants
 * Central configuration for colors, typography, spacing, and other design tokens
 * Used across all shared components for consistency
 */

export const theme = {
  // Color Palette
  colors: {
    primary: {
      main: '#3901FF',
      dark: '#2d00cc',
      light: '#5a33ff',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FF8C00',
      dark: '#e67e00',
      light: '#ffa333',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2cc26a',
      dark: '#23a057',
      light: '#4cd685',
      contrastText: '#ffffff',
    },
    error: {
      main: '#e60000',
      dark: '#b80000',
      light: '#ff3333',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ffa500',
      dark: '#cc8400',
      light: '#ffb733',
      contrastText: '#000000',
    },
    white: '#ffffff',
    black: '#000000',
    gray: {
      50: '#f9f9f9',
      100: '#f4f4f4',
      200: '#e8e8e8',
      300: '#d4d4d4',
      400: '#b0b0b0',
      500: '#808080',
      600: '#5a5a5a',
      700: '#4f4f4e',
      800: '#2b2b2b',
      900: '#1a1a1a',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
      alt: '#f9f9f9',
    },
    text: {
      primary: '#2b2b2b',
      secondary: '#808080',
      disabled: '#b0b0b0',
    },
  },

  // Typography
  typography: {
    fontFamily: "'Roboto', sans-serif",
    fontSize: {
      xs: '0.523rem',    // ~8.5px - for very small text
      sm: '0.688rem',    // ~11px - for labels
      base: '0.75rem',   // 12px - base font size
      md: '0.813rem',    // ~13px - for body text
      lg: '0.938rem',    // 15px - for larger text
      xl: '1rem',        // 16px - for headings
      '2xl': '1.25rem',  // 20px
      '3xl': '1.5rem',   // 24px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: '0.688rem',
      normal: '0.813rem',
      relaxed: '1rem',
    },
  },

  // Spacing Scale (in rem)
  spacing: {
    xs: '0.3rem',      // ~5px
    sm: '0.6rem',      // ~10px
    md: '0.8rem',      // ~13px
    lg: '1rem',        // 16px
    xl: '1.5rem',      // 24px
    '2xl': '2rem',     // 32px
    '3xl': '3rem',     // 48px
  },

  // Border Radius
  borderRadius: {
    none: '0',
    sm: '0.3rem',      // ~5px
    md: '0.375rem',    // 6px
    lg: '0.5rem',      // 8px
    full: '9999px',
  },

  // Shadows
  shadows: {
    none: 'none',
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 2px 8px rgba(0, 0, 0, 0.1)',
    lg: '0 4px 12px rgba(0, 0, 0, 0.15)',
    xl: '0 8px 24px rgba(0, 0, 0, 0.2)',
    primary: '0 2px 8px rgba(57, 1, 255, 0.08)',
    success: '0 2px 8px rgba(44, 194, 106, 0.08)',
    error: '0 2px 8px rgba(230, 0, 0, 0.08)',
  },

  // Transitions
  transitions: {
    duration: {
      fast: '150ms',
      normal: '200ms',
      slow: '300ms',
    },
    timing: {
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
    },
  },

  // Component-specific sizes
  components: {
    button: {
      height: {
        sm: '1.5rem',    // 24px
        md: '1.7rem',    // ~27px
        lg: '2.5rem',    // 40px
      },
      padding: {
        sm: '0.3rem 0.6rem',
        md: '0.5rem 1rem',
        lg: '0.75rem 1.5rem',
      },
    },
    input: {
      height: '1.7rem',        // ~27px - standard input height
      borderWidth: '0.692008px', // ~1.1px - from existing design
      borderColor: '#808080',
      fontSize: '0.75rem',     // 12px
    },
  },

  // Z-index scale
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
};

export default theme;
